import React, { useState, ReactNode } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaDesktop, FaTabletAlt, FaMobile, FaTimes, FaRedo, FaEllipsisV, FaLock, FaBars } from 'react-icons/fa';
import Button from './Button';
import Layout from './Layout';

// Animaciones
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

const DemoContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const DemoHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h1 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: var(--gray-dark);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.1rem;
    color: var(--gray);
    max-width: 800px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const DeviceSelector = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 1rem;
  
  @media (max-width: 576px) {
    gap: 0.5rem;
  }
`;

const DeviceOption = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  ${props => props.active ? css`
    background-color: rgba(0, 123, 255, 0.1);
    color: var(--blue);
    
    svg {
      color: var(--blue);
    }
  ` : css`
    background-color: transparent;
    color: var(--gray);
    
    &:hover {
      background-color: #f5f5f5;
    }
  `}
  
  svg {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  span {
    font-size: 0.9rem;
    font-weight: 500;
  }
  
  @media (max-width: 576px) {
    padding: 0.5rem;
    
    svg {
      font-size: 1.2rem;
    }
    
    span {
      font-size: 0.8rem;
    }
  }
`;

const DeviceFrame = styled.div<{ device: 'desktop' | 'tablet' | 'mobile' }>`
  width: 100%;
  margin: 0 auto;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.6s ease-out;
  background: white;
  
  ${props => props.device === 'desktop' && css`
    max-width: 100%;
  `}
  
  ${props => props.device === 'tablet' && css`
    max-width: 768px;
  `}
  
  ${props => props.device === 'mobile' && css`
    max-width: 375px;
  `}
`;

const BrowserBar = styled.div`
  background: linear-gradient(to bottom, #f6f6f6, #e8e8e8);
  border-bottom: 1px solid #c8c8c8;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 576px) {
    padding: 8px 10px;
  }
`;

const BrowserControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  svg {
    font-size: 16px;
    color: #666;
    cursor: pointer;
    transition: color 0.2s;
    
    &:hover {
      color: #333;
    }
  }
  
  @media (max-width: 576px) {
    gap: 8px;
    
    svg {
      font-size: 14px;
    }
  }
`;

const UrlBar = styled.div`
  background-color: white;
  border: 1px solid #c8c8c8;
  border-radius: 20px;
  padding: 10px 16px;
  flex-grow: 1;
  margin: 0 12px;
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
  font-weight: 500;
  
  svg {
    color: var(--blue);
    margin-right: 8px;
    font-size: 14px;
  }
  
  @media (max-width: 576px) {
    padding: 6px 10px;
    margin: 0 6px;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    svg {
      font-size: 12px;
      margin-right: 4px;
    }
  }
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #ddd;
  }
  
  svg {
    font-size: 14px;
    color: #666;
  }
  
  @media (max-width: 576px) {
    width: 20px;
    height: 20px;
    
    svg {
      font-size: 12px;
    }
  }
`;

const DeviceContent = styled.div`
  height: auto;
  overflow: hidden;
  position: relative;
`;

const BackToService = styled.div`
  margin-top: 2rem;
  text-align: center;
  
  a {
    display: inline-flex;
    align-items: center;
    color: var(--blue);
    text-decoration: none;
    font-weight: 500;
    
    svg {
      margin-right: 0.5rem;
    }
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const NavMenu = styled.div<{ device: 'desktop' | 'tablet' | 'mobile' }>`
  background-color: #333;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  border-bottom: 1px solid #444;
  
  ${props => (props.device === 'tablet' || props.device === 'mobile') && css`
    padding: 10px 15px;
  `}
  
  ${props => props.device === 'mobile' && css`
    padding: 8px 12px;
  `}
`;

const NavBrand = styled.div<{ device: 'desktop' | 'tablet' | 'mobile' }>`
  color: var(--white);
  font-weight: 600;
  font-size: 18px;
  
  ${props => props.device === 'tablet' && css`
    font-size: 16px;
  `}
  
  ${props => props.device === 'mobile' && css`
    font-size: 14px;
  `}
`;

const MobileMenuToggle = styled.button`
  background: none;
  border: none;
  color: var(--white);
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

// Versión de links para escritorio
const DesktopLinks = styled.div`
  display: flex;
  gap: 20px;
  
  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    font-size: 14px;
    transition: color 0.2s;
    
    &:hover {
      color: var(--white);
    }
  }
`;

// Contenedor para el botón de Reservar en escritorio
const DesktopCta = styled.div`
  margin-left: 20px;
`;

const NavCta = styled.a<{ device: 'desktop' | 'tablet' | 'mobile' }>`
  background-color: var(--blue);
  color: var(--white) !important;
  padding: 6px 15px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  
  &:hover {
    background-color: var(--indigo);
  }
  
  ${props => props.device === 'tablet' && css`
    padding: 5px 12px;
    font-size: 13px;
  `}
  
  ${props => props.device === 'mobile' && css`
    padding: 4px 10px;
    font-size: 12px;
  `}
`;

const DemoControls = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  p {
    color: var(--gray);
    margin: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
    
    p {
      margin-bottom: 0.5rem;
    }
  }
`;

// Versión de menú móvil contenido dentro del navegador de la demo
const MobileNavOverlay = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`;

const MobileNavContent = styled.div`
  width: 100%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  backdrop-filter: blur(5px);
`;

const MobileNavHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const MobileNavBrand = styled.div`
  color: var(--white);
  font-weight: 600;
  font-size: 16px;
`;

const MobileNavCloseButton = styled.button`
  background: none;
  border: none;
  color: var(--white);
  font-size: 20px;
  cursor: pointer;
  padding: 0;
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 25px;
  width: 100%;
  
  a {
    color: var(--white);
    text-decoration: none;
    font-size: 14px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      color: var(--blue);
    }
  }
`;

const MobileNavCta = styled.a`
  background-color: var(--blue);
  color: var(--white);
  padding: 10px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  text-align: center;
  
  &:hover {
    background-color: var(--indigo);
  }
`;

interface DemoViewerProps {
  title: string;
  subtitle: string;
  domainName: string;
  children: ReactNode;
  serviceLink: string;
  serviceName: string;
}

const DemoViewerContent: React.FC<DemoViewerProps> = ({
  title,
  subtitle,
  domainName,
  children,
  serviceLink,
  serviceName
}) => {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
  };
  
  return (
    <DemoContainer>
      <DemoHeader>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </DemoHeader>
      
      <DeviceSelector>
        <DeviceOption 
          active={device === 'desktop'} 
          onClick={() => { setDevice('desktop'); closeMenu(); }}
        >
          <FaDesktop />
          <span>Desktop</span>
        </DeviceOption>
        <DeviceOption 
          active={device === 'tablet'} 
          onClick={() => { setDevice('tablet'); closeMenu(); }}
        >
          <FaTabletAlt />
          <span>Tablet</span>
        </DeviceOption>
        <DeviceOption 
          active={device === 'mobile'} 
          onClick={() => { setDevice('mobile'); closeMenu(); }}
        >
          <FaMobile />
          <span>Mobile</span>
        </DeviceOption>
      </DeviceSelector>
      
      <DeviceFrame device={device}>
        <BrowserBar>
          <BrowserControls>
            <FaArrowLeft />
            <FaArrowRight />
            <FaRedo />
          </BrowserControls>
          <UrlBar>
            <FaLock />
            {domainName}
          </UrlBar>
          <CloseButton>
            <FaTimes />
          </CloseButton>
        </BrowserBar>
        
        <DeviceContent>
          <NavMenu device={device}>
            <NavBrand device={device}>Tu Marca</NavBrand>
            
            {device === 'desktop' ? (
              <>
                <DesktopLinks>
                  <a href="#">Inicio</a>
                  <a href="#">Servicios</a>
                  <a href="#">Sobre Nosotros</a>
                  <a href="#">Galería</a>
                  <a href="#">Blog</a>
                  <a href="#">Contacto</a>
                </DesktopLinks>
                <DesktopCta>
                  <NavCta device={device} href="#">Reservar</NavCta>
                </DesktopCta>
              </>
            ) : (
              <MobileMenuToggle onClick={toggleMenu}>
                <FaBars />
              </MobileMenuToggle>
            )}
          </NavMenu>
          
          {/* Menú móvil dentro del contenedor del navegador */}
          {(device === 'tablet' || device === 'mobile') && (
            <MobileNavOverlay isOpen={isMenuOpen}>
              <MobileNavContent>
                <MobileNavHeader>
                  <MobileNavBrand>Tu Marca</MobileNavBrand>
                  <MobileNavCloseButton onClick={closeMenu}>
                    <FaTimes />
                  </MobileNavCloseButton>
                </MobileNavHeader>
                <MobileNavLinks>
                  <a href="#" onClick={closeMenu}>Inicio</a>
                  <a href="#" onClick={closeMenu}>Servicios</a>
                  <a href="#" onClick={closeMenu}>Sobre Nosotros</a>
                  <a href="#" onClick={closeMenu}>Galería</a>
                  <a href="#" onClick={closeMenu}>Blog</a>
                  <a href="#" onClick={closeMenu}>Contacto</a>
                </MobileNavLinks>
                <MobileNavCta href="#" onClick={closeMenu}>Reservar</MobileNavCta>
              </MobileNavContent>
            </MobileNavOverlay>
          )}
          
          {/* Contenido de la demo */}
          {children}
        </DeviceContent>
      </DeviceFrame>
      
      <BackToService>
        <Link to={serviceLink}>
          <FaArrowLeft />
          Volver a servicios para {serviceName}
        </Link>
      </BackToService>
      
      <DemoControls>
        <p>Esta es una demo interactiva. Los enlaces y formularios no son funcionales.</p>
        <Button as="a" href="/reserva" size="small">Quiero una web así</Button>
      </DemoControls>
    </DemoContainer>
  );
};

// Envolviendo el componente con Layout para mantener la estructura del sitio
const DemoViewer: React.FC<DemoViewerProps> = (props) => {
  return (
    <Layout>
      <DemoViewerContent {...props} />
    </Layout>
  );
};

export default DemoViewer; 